import React, { useState, useEffect } from "react";
import axios from "axios";
import proxy from "../../proxy";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  SimpleGrid,
  Textarea,
  Text,
  Box
} from "@chakra-ui/react";

const DividendModal = ({ isOpen, onClose, dividend, onSave }) => {
  const [formData, setFormData] = useState({
    date: "",
    ticker: "",
    description: "",
    amount: "",
    tax: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (dividend) {
      setFormData({
        date: dividend.date || "",
        ticker: dividend.ticker || "",
        description: dividend.description || "",
        amount: dividend.amount || "",
        tax: dividend.tax || "",
      });
    } else {
      const today = new Date().toISOString().split("T")[0];
      setFormData({
        date: today,
        ticker: "",
        description: "",
        amount: "",
        tax: "",
      });
    }
  }, [dividend, isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.date || !formData.ticker || !formData.amount) {
      toast({
        title: "Missing fields",
        description: "Date, Ticker, and Amount are required",
        status: "warning",
        duration: 3000,
        position: "top",
      });
      return;
    }

    setIsLoading(true);

    try {
      if (dividend) {
        // Update
        await axios.post(`${proxy}server_mts/admin/UpdateDividend.php`, {
          id: dividend.id,
          ...formData,
        });
        toast({
          title: "Dividend updated successfully",
          status: "success",
          duration: 2000,
          position: "top",
        });
      } else {
        // Add
        await axios.post(`${proxy}server_mts/admin/AddDividend.php`, formData);
        toast({
          title: "Dividend added successfully",
          status: "success",
          duration: 2000,
          position: "top",
        });
      }

      onSave();
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      toast({
        title: "Error saving dividend",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Calculează net amount
  const netAmount = (Number(formData.amount) - Number(formData.tax || 0)).toFixed(4);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent bg="gray.800" borderColor="whiteAlpha.200" border="1px solid">
        <ModalHeader color="white">
          {dividend ? "Edit Dividend" : "Add New Dividend"}
        </ModalHeader>
        <ModalCloseButton color="gray.400" />
        <ModalBody>
          <VStack spacing={4}>
            {/* Date & Ticker */}
            <SimpleGrid columns={2} spacing={4} w="full">
              <FormControl isRequired>
                <FormLabel color="gray.300" fontSize="sm">
                  Date
                </FormLabel>
                <Input
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color="gray.300" fontSize="sm">
                  Ticker Symbol
                </FormLabel>
                <Input
                  name="ticker"
                  value={formData.ticker}
                  onChange={handleChange}
                  placeholder="e.g., O"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  textTransform="uppercase"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>
            </SimpleGrid>

            {/* Description */}
            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                Description
              </FormLabel>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Cash Dividend USD 0.2685 per Share (Ordinary Dividend)"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                rows={3}
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              />
            </FormControl>

            {/* Amount & Tax */}
            <SimpleGrid columns={2} spacing={4} w="full">
              <FormControl isRequired>
                <FormLabel color="gray.300" fontSize="sm">
                  Amount ($)
                </FormLabel>
                <Input
                  name="amount"
                  type="number"
                  step="0.0001"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.36"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300" fontSize="sm">
                  Tax ($)
                </FormLabel>
                <Input
                  name="tax"
                  type="number"
                  step="0.0001"
                  value={formData.tax}
                  onChange={handleChange}
                  placeholder="0.11"
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                />
              </FormControl>
            </SimpleGrid>

            {/* Net Amount Display */}
            {formData.amount && (
              <Box
                w="full"
                p={3}
                bg="whiteAlpha.50"
                borderRadius="md"
                border="1px solid"
                borderColor="whiteAlpha.200"
              >
                <Text fontSize="xs" color="gray.400" mb={1}>
                  Net Amount (after tax)
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="teal.300">
                  ${netAmount}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={isLoading}
          >
            {dividend ? "Update" : "Add"} Dividend
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DividendModal;